/* eslint-disable jsx-a11y/media-has-caption */
/* eslint-disable no-nested-ternary */
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import EmojiPicker, { EmojiClickData } from "emoji-picker-react";
import {
  FiFileText,
  FiMic,
  FiPaperclip,
  FiSend,
  FiSquare,
  FiSmile,
  FiX,
} from "react-icons/fi";
import { IoPersonOutline } from "react-icons/io5";
import { CircularProgress } from "@material-ui/core";
import { WhatsAppMsg } from "services/api/AppWeb_WhatsApp/types";
import {
  useEnviarArquivo,
  useEnviarMensagem,
  useEditarMensagem,
  useExcluirMensagem,
  useMarcarLido,
  useWhatsAppConversas,
} from "services/api/AppWeb_WhatsApp/useWhatsAppConversas";
import { MsgList } from "screens/whatsapp/components/MsgBubble";
import useAutoResizeTextarea from "hooks/useAutoResizeTextarea";
import * as Styles from "./styles";

interface Props {
  open: boolean;
  onClose: () => void;
  leadName: string;
  leadPhone: string;
  idLeadCorretor365: string;
  leadPhotoUrl?: string;
}

type AttachmentKind = "image" | "video" | "audio" | "pdf" | "document";

interface PendingAttachment {
  file: File;
  kind: AttachmentKind;
  previewUrl?: string;
}

function getAttachmentKind(file: File): AttachmentKind {
  if (file.type.startsWith("image/")) return "image";
  if (file.type.startsWith("video/")) return "video";
  if (file.type.startsWith("audio/")) return "audio";
  if (file.type === "application/pdf") return "pdf";
  return "document";
}

function formatFileSize(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

const EnviarWhatsAppModal: React.FC<Props> = ({
  open,
  onClose,
  leadName,
  leadPhone,
  idLeadCorretor365,
  leadPhotoUrl,
}) => {
  const [mensagem, setMensagem] = useState("");
  const [dialogShake, setDialogShake] = useState(false);
  const [photoBroken, setPhotoBroken] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [editingMsgId, setEditingMsgId] = useState<string | null>(null);
  const [editingMsgPreview, setEditingMsgPreview] = useState("");
  const [pendingAttachment, setPendingAttachment] =
    useState<PendingAttachment | null>(null);
  const [recording, setRecording] = useState(false);
  const [recSecs, setRecSecs] = useState(0);
  const recRef = useRef<MediaRecorder | null>(null);
  const recChunks = useRef<Blob[]>([]);
  const recTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const recStream = useRef<MediaStream | null>(null);
  const shouldSendRecording = useRef(true);
  const enviarMensagem = useEnviarMensagem();
  const enviarArquivo = useEnviarArquivo();
  const editarMensagem = useEditarMensagem();
  const excluirMensagem = useExcluirMensagem();
  const marcarLido = useMarcarLido();
  const conversaLeadId = open ? idLeadCorretor365 : "";
  const { data: conversa = [], isLoading: loadingConversa } =
    useWhatsAppConversas(conversaLeadId);
  useAutoResizeTextarea(inputRef, mensagem, {
    maxLines: 7,
    enabled: open && !recording,
  });

  useEffect(() => {
    setPhotoBroken(false);
  }, [leadPhotoUrl, open]);

  useEffect(
    () => () => {
      if (pendingAttachment?.previewUrl) {
        URL.revokeObjectURL(pendingAttachment.previewUrl);
      }
      if (recTimer.current) {
        clearInterval(recTimer.current);
      }
      if (recRef.current && recRef.current.state !== "inactive") {
        shouldSendRecording.current = false;
        recRef.current.stop();
      }
      if (recStream.current) {
        recStream.current.getTracks().forEach((track) => track.stop());
        recStream.current = null;
      }
    },
    [pendingAttachment]
  );

  useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    });
  }, [open, conversa]);

  useEffect(() => {
    if (!open || !idLeadCorretor365) return;
    marcarLido(idLeadCorretor365);
    // Marca como lido apenas ao abrir / trocar de lead — não a cada refetch
  }, [open, idLeadCorretor365]);

  useEffect(() => {
    if (!emojiOpen) return undefined;

    function handleClickOutside(e: MouseEvent) {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target as Node)
      ) {
        setEmojiOpen(false);
      }
    }

    function handleEsc(e: KeyboardEvent) {
      if (e.key === "Escape") setEmojiOpen(false);
    }

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEsc);
    };
  }, [emojiOpen]);

  const handleBDClick = () => {
    setDialogShake(true);
    const t = setTimeout(() => setDialogShake(false), 300);
    return () => clearTimeout(t);
  };

  const handleEnviar = async () => {
    if (recording || !mensagem.trim() || !idLeadCorretor365) return;

    const texto = mensagem.trim();

    if (editingMsgId) {
      try {
        await editarMensagem.mutateAsync({
          leadId: idLeadCorretor365,
          idMensagem: editingMsgId,
          mensagem: texto,
        });
        setMensagem("");
        setEditingMsgId(null);
        setEditingMsgPreview("");
      } catch {
        // Erro já tratado no hook
      }
      return;
    }

    try {
      await enviarMensagem.mutateAsync({
        leadId: idLeadCorretor365,
        mensagem: texto,
      });
      setMensagem("");
      setEmojiOpen(false);
    } catch {
      // Erro já tratado pelo onError do hook
    }
  };

  const clearPendingAttachment = () => {
    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return null;
    });
  };

  const setPendingAttachmentFromFile = (file: File) => {
    const kind = getAttachmentKind(file);
    const previewUrl =
      kind === "image" || kind === "video" || kind === "audio"
        ? URL.createObjectURL(file)
        : undefined;

    setPendingAttachment((prev) => {
      if (prev?.previewUrl) URL.revokeObjectURL(prev.previewUrl);
      return { file, kind, previewUrl };
    });
  };

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    e.target.value = "";
    setPendingAttachmentFromFile(file);
    setEditingMsgId(null);
    setEditingMsgPreview("");
    setMensagem("");
    setEmojiOpen(false);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (recording || sending) return;
    const fileItem = Array.from(e.clipboardData.items).find(
      (item) => item.kind === "file"
    );
    const file = fileItem?.getAsFile();
    if (!file) return;

    e.preventDefault();
    setPendingAttachmentFromFile(file);
    setEditingMsgId(null);
    setEditingMsgPreview("");
    setMensagem("");
    setEmojiOpen(false);
  };

  const sendPendingAttachment = async () => {
    if (!pendingAttachment || !idLeadCorretor365) return;

    try {
      setEditingMsgId(null);
      setEditingMsgPreview("");
      setMensagem("");
      await enviarArquivo.mutateAsync({
        leadId: idLeadCorretor365,
        file: pendingAttachment.file,
      });
      clearPendingAttachment();
    } catch {
      // Erro já tratado pelo onError do hook
    }
  };

  const handleCancel = () => {
    if (recording) {
      shouldSendRecording.current = false;
      if (recTimer.current) {
        clearInterval(recTimer.current);
        recTimer.current = null;
      }
      if (recRef.current && recRef.current.state !== "inactive") {
        recRef.current.stop();
      }
      if (recStream.current) {
        recStream.current.getTracks().forEach((track) => track.stop());
        recStream.current = null;
      }
      setRecording(false);
      setRecSecs(0);
    }
    setMensagem("");
    setEditingMsgId(null);
    setEditingMsgPreview("");
    setEmojiOpen(false);
    clearPendingAttachment();
    onClose();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (recording) return;
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  };

  const handleEmojiSelect = (emojiData: EmojiClickData) => {
    if (recording) return;
    const input = inputRef.current;
    const { emoji } = emojiData;

    if (!input) {
      setMensagem((prev) => `${prev}${emoji}`);
      return;
    }

    const start = input.selectionStart ?? mensagem.length;
    const end = input.selectionEnd ?? mensagem.length;
    const nextText = `${mensagem.slice(0, start)}${emoji}${mensagem.slice(
      end
    )}`;

    setMensagem(nextText);
    requestAnimationFrame(() => {
      input.focus();
      const pos = start + emoji.length;
      input.setSelectionRange(pos, pos);
    });
  };

  const handleDeleteMessage = (msg: WhatsAppMsg) => {
    if (msg.direcao !== "saida" || !idLeadCorretor365) return;
    if (msg.id.startsWith("tmp-")) {
      toast.warn("Aguarde o envio antes de excluir a mensagem.");
      return;
    }

    excluirMensagem.mutate(
      { leadId: idLeadCorretor365, idMensagem: msg.id },
      {
        onSuccess: () => {
          if (editingMsgId === msg.id) {
            setEditingMsgId(null);
            setEditingMsgPreview("");
            setMensagem("");
          }
        },
      }
    );
  };

  const handleEditMessage = (msg: WhatsAppMsg) => {
    if (msg.direcao !== "saida") return;
    if (msg.id.startsWith("tmp-")) {
      toast.warn("Aguarde o envio antes de editar a mensagem.");
      return;
    }
    if (msg.mediaUrl || msg.mediaType || !msg.mensagem?.trim()) return;

    clearPendingAttachment();
    setEditingMsgId(msg.id);
    setEditingMsgPreview(msg.mensagem || "");
    setMensagem(msg.mensagem);
    setEmojiOpen(false);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const cancelEditingMessage = () => {
    setEditingMsgId(null);
    setEditingMsgPreview("");
    setMensagem("");
  };

  const toggleRecording = async () => {
    if (sending || pendingAttachment) return;

    if (recording) {
      shouldSendRecording.current = true;
      if (recTimer.current) {
        clearInterval(recTimer.current);
        recTimer.current = null;
      }
      if (recRef.current && recRef.current.state !== "inactive") {
        recRef.current.stop();
      }
      setRecording(false);
      setRecSecs(0);
      return;
    }

    try {
      clearPendingAttachment();
      setEmojiOpen(false);
      setEditingMsgId(null);
      setEditingMsgPreview("");
      setMensagem("");

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      recStream.current = stream;

      const recorder = new MediaRecorder(stream);
      recRef.current = recorder;
      recChunks.current = [];
      shouldSendRecording.current = true;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) recChunks.current.push(event.data);
      };

      recorder.onstop = async () => {
        recStream.current?.getTracks().forEach((track) => track.stop());
        recStream.current = null;

        if (!shouldSendRecording.current || !idLeadCorretor365) return;

        const blob = new Blob(recChunks.current, { type: "audio/webm" });
        if (blob.size === 0) return;

        const file = new File([blob], "audio.webm", { type: "audio/webm" });
        try {
          await enviarArquivo.mutateAsync({
            leadId: idLeadCorretor365,
            file,
          });
        } catch {
          // Erro já tratado pelo onError do hook
        }
      };

      recorder.start();
      setRecording(true);
      setRecSecs(0);
      recTimer.current = setInterval(
        () => setRecSecs((secs) => secs + 1),
        1000
      );
    } catch {
      toast.error("Sem permissão para microfone");
    }
  };

  const sending =
    enviarMensagem.isLoading ||
    enviarArquivo.isLoading ||
    editarMensagem.isLoading ||
    excluirMensagem.isLoading;

  return (
    <Styles.Modal
      open={open}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") handleCancel();
        else handleBDClick();
      }}
      className={`shake-${dialogShake}`}
    >
      <Styles.Window>
        <Styles.Header>
          <Styles.HeaderLeft>
            {leadPhotoUrl && !photoBroken ? (
              <Styles.AvatarImage
                src={leadPhotoUrl}
                alt={leadName}
                onError={() => setPhotoBroken(true)}
              />
            ) : (
              <Styles.AvatarFallback>
                <IoPersonOutline size={18} />
              </Styles.AvatarFallback>
            )}
            <Styles.HeaderMeta>
              <Styles.LeadName>{leadName || "Contato"}</Styles.LeadName>
              <Styles.LeadStatus>{leadPhone || "WhatsApp"}</Styles.LeadStatus>
            </Styles.HeaderMeta>
          </Styles.HeaderLeft>

          <Styles.HeaderActions>
            <Styles.CloseButton type="button" onClick={handleCancel}>
              <FiX size={18} />
            </Styles.CloseButton>
          </Styles.HeaderActions>
        </Styles.Header>

        <Styles.ChatBody>
          <Styles.MessagesScroller>
            {loadingConversa ? (
              <Styles.CenterHint>Carregando conversa...</Styles.CenterHint>
            ) : conversa.length === 0 ? (
              <Styles.CenterHint>
                Nenhuma mensagem ainda. Envie a primeira mensagem.
              </Styles.CenterHint>
            ) : (
              <MsgList
                msgs={conversa}
                onEditMessage={handleEditMessage}
                onDeleteMessage={handleDeleteMessage}
                editingMessageId={editingMsgId}
              />
            )}
            <div ref={bottomRef} />
          </Styles.MessagesScroller>
        </Styles.ChatBody>

        {editingMsgId && (
          <Styles.EditingMessageBar>
            <Styles.EditingMessageText>
              Editando: {editingMsgPreview || "mensagem"}
            </Styles.EditingMessageText>
            <Styles.EditingMessageCancel
              type="button"
              onClick={cancelEditingMessage}
              disabled={sending}
            >
              Cancelar
            </Styles.EditingMessageCancel>
          </Styles.EditingMessageBar>
        )}

        {pendingAttachment && (
          <Styles.AttachmentPreviewBar>
            <Styles.AttachmentPreviewMedia>
              {pendingAttachment.kind === "image" &&
                pendingAttachment.previewUrl && (
                  <img
                    src={pendingAttachment.previewUrl}
                    alt={pendingAttachment.file.name}
                  />
                )}
              {pendingAttachment.kind === "video" &&
                pendingAttachment.previewUrl && (
                  <video src={pendingAttachment.previewUrl} controls />
                )}
              {pendingAttachment.kind === "audio" &&
                pendingAttachment.previewUrl && (
                  <audio src={pendingAttachment.previewUrl} controls />
                )}
              {(pendingAttachment.kind === "pdf" ||
                pendingAttachment.kind === "document") && (
                <Styles.AttachmentDocIcon>
                  <FiFileText size={18} />
                </Styles.AttachmentDocIcon>
              )}
            </Styles.AttachmentPreviewMedia>
            <Styles.AttachmentPreviewInfo>
              <Styles.AttachmentPreviewName>
                {pendingAttachment.file.name}
              </Styles.AttachmentPreviewName>
              <Styles.AttachmentPreviewMeta>
                {pendingAttachment.kind.toUpperCase()} ·{" "}
                {formatFileSize(pendingAttachment.file.size)}
              </Styles.AttachmentPreviewMeta>
            </Styles.AttachmentPreviewInfo>
            <Styles.AttachmentPreviewActions>
              <Styles.AttachmentActionButton
                type="button"
                onClick={clearPendingAttachment}
                disabled={sending}
              >
                Cancelar
              </Styles.AttachmentActionButton>
              <Styles.AttachmentActionButton
                type="button"
                primary
                onClick={sendPendingAttachment}
                disabled={sending}
              >
                {sending ? "Enviando..." : "Enviar"}
              </Styles.AttachmentActionButton>
            </Styles.AttachmentPreviewActions>
          </Styles.AttachmentPreviewBar>
        )}

        <Styles.Composer>
          <input
            ref={fileRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleFile}
            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.xls,.xlsx"
          />

          <Styles.AttachButton
            type="button"
            aria-label="Anexar arquivo"
            onClick={() => fileRef.current?.click()}
            disabled={sending || recording}
          >
            <FiPaperclip size={18} />
          </Styles.AttachButton>
          <Styles.EmojiPickerWrapper ref={emojiPickerRef}>
            <Styles.IconCircleButton
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              disabled={sending || recording}
              aria-label="Abrir seletor de emoji"
              aria-expanded={emojiOpen}
            >
              <FiSmile size={18} />
            </Styles.IconCircleButton>

            {emojiOpen && (
              <Styles.EmojiPopover>
                <EmojiPicker
                  onEmojiClick={handleEmojiSelect}
                  width="100%"
                  height={340}
                  lazyLoadEmojis
                  previewConfig={{ showPreview: false }}
                />
              </Styles.EmojiPopover>
            )}
          </Styles.EmojiPickerWrapper>
          {recording ? (
            <Styles.RecordingBar>
              <Styles.RecordDot />
              <Styles.RecordTime>
                {Math.floor(recSecs / 60)
                  .toString()
                  .padStart(2, "0")}
                :{(recSecs % 60).toString().padStart(2, "0")}
              </Styles.RecordTime>
              <Styles.RecordLabel>Gravando...</Styles.RecordLabel>
            </Styles.RecordingBar>
          ) : (
            <Styles.MessageInput
              ref={inputRef}
              rows={1}
              placeholder={
                editingMsgId
                  ? "Edite sua mensagem..."
                  : "Escreva sua mensagem..."
              }
              value={mensagem}
              onChange={(e) => setMensagem(e.target.value)}
              onPaste={handlePaste}
              onKeyDown={handleInputKeyDown}
              maxLength={5000}
              disabled={sending}
            />
          )}
          <Styles.SendButton
            type="button"
            onClick={mensagem.trim() ? handleEnviar : toggleRecording}
            disabled={sending || !!pendingAttachment}
            aria-label={mensagem.trim() ? "Enviar mensagem" : "Gravar áudio"}
            variant={recording ? "danger" : undefined}
          >
            {sending ? (
              <CircularProgress size={16} color="inherit" />
            ) : mensagem.trim() ? (
              <FiSend size={16} style={{ transform: "translateX(1px)" }} />
            ) : recording ? (
              <FiSquare size={16} />
            ) : (
              <FiMic size={18} />
            )}
          </Styles.SendButton>
        </Styles.Composer>
      </Styles.Window>
    </Styles.Modal>
  );
};

export default EnviarWhatsAppModal;
